interface SummaryMetrics {
  incoming_messages: number;
  outgoing_messages: number;
  outgoing_segments: number;
  opt_out_count: number;
  failed_count: number;
  spam_filtered_count: number;
}

interface SummaryCardsProps {
  rangeData: SummaryMetrics;
  todayData: SummaryMetrics;
  lifetimeData: SummaryMetrics;
  loading?: boolean;
}

interface PeriodCardProps {
  title: string;
  data: SummaryMetrics;
  loading?: boolean;
}

const formatNumber = (num: number) => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}K`;
  }
  return num.toLocaleString();
};

const formatRate = (count: number, total: number) => {
  if (total <= 0) return '0.00%';
  return `${(count / total * 100).toFixed(2)}%`;
};

// ============================================================================
// Rate highlight thresholds & colors
// ============================================================================
// Thresholds are in PERCENT and use at-or-above (">=") comparisons.
// Below `warn` → normal; at/over `warn` → warning; at/over `error` → error.
const RATE_THRESHOLDS = {
  optOut: { warn: 2, error: 6 },     // Opt-Out Rate
  error:  { warn: 3, error: 5 },     // Error Rate
  spam:   { warn: 0.01, error: 0.5 }, // Spam Rate
} as const;

const RATE_COLORS = {
  normal: 'text-gray-900',
  warn: 'text-amber-600',
  error: 'text-red-600',
} as const;

type Thresholds = { warn: number; error: number };

// Pick the text color class for a rate given its thresholds.
// No data (total <= 0) always renders normal.
const rateColorClass = (count: number, total: number, thresholds: Thresholds): string => {
  if (total <= 0) return RATE_COLORS.normal;
  const rate = (count / total) * 100;
  if (rate >= thresholds.error) return RATE_COLORS.error;
  if (rate >= thresholds.warn) return RATE_COLORS.warn;
  return RATE_COLORS.normal;
};

function MetricRow({ label, value, loading }: { label: string; value: number; loading?: boolean }) {
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-600">{label}</span>
      {loading ? (
        <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
      ) : (
        <span className="text-sm font-semibold text-gray-900">{formatNumber(value)}</span>
      )}
    </div>
  );
}

function RateRow({
  label,
  count,
  total,
  thresholds,
  loading,
  title,
}: {
  label: string;
  count: number;
  total: number;
  thresholds: Thresholds;
  loading?: boolean;
  title?: string;
}) {
  const colorClass = rateColorClass(count, total, thresholds);
  return (
    <div className="flex items-center justify-between py-2">
      <span className="text-sm text-gray-600" title={title}>{label}</span>
      {loading ? (
        <div className="h-5 w-16 bg-gray-200 rounded animate-pulse" />
      ) : (
        <span className={`text-sm font-semibold ${colorClass}`}>{formatRate(count, total)}</span>
      )}
    </div>
  );
}

function PeriodCard({ title, data, loading }: PeriodCardProps) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{title}</h3>
      </div>
      <div className="px-4 py-2 divide-y divide-gray-100">
        <MetricRow label="Messages Sent" value={data.outgoing_messages} loading={loading} />
        <MetricRow label="Messages Received" value={data.incoming_messages} loading={loading} />
        <MetricRow label="Total Segments" value={data.outgoing_segments} loading={loading} />
        <RateRow
          label="Opt-Out Rate"
          count={data.opt_out_count}
          total={data.outgoing_messages}
          thresholds={RATE_THRESHOLDS.optOut}
          loading={loading}
        />
        <RateRow
          label="Error Rate"
          count={data.failed_count}
          total={data.outgoing_messages}
          thresholds={RATE_THRESHOLDS.error}
          loading={loading}
        />
        <RateRow
          label="Spam Rate"
          count={data.spam_filtered_count}
          total={data.outgoing_messages}
          thresholds={RATE_THRESHOLDS.spam}
          loading={loading}
          title="Messages filtered by carriers as spam (Twilio error 30007). A subset of the Error Rate above."
        />
      </div>
    </div>
  );
}

export function SummaryCards({ rangeData, todayData, lifetimeData, loading }: SummaryCardsProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <PeriodCard title="Today" data={todayData} loading={loading} />
      <PeriodCard title="Selected Period" data={rangeData} loading={loading} />
      <PeriodCard title="Lifetime" data={lifetimeData} loading={loading} />
    </div>
  );
}
